"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Users, Target, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { LandingSectionBadge } from "./LandingSectionBadge";

const values = [
  {
    icon: Target,
    title: "Mission-Driven",
    description:
      "We're committed to helping businesses make smarter financial decisions through innovative technology.",
  },
  {
    icon: Zap,
    title: "Innovation First",
    description:
      "We continuously evolve our platform with cutting-edge features and AI-powered insights.",
  },
  {
    icon: Users,
    title: "Customer-Centric",
    description:
      "Your success is our success. We build every feature with your needs in mind.",
  },
];

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.42, 0, 0.58, 1] as const, // easeOut cubic bezier
      },
    },
  };

  const valueColors = [
    "from-blue-500 to-cyan-500",
    "from-blue-600 to-blue-400",
    "from-cyan-500 to-blue-500",
  ];

  return (
    <section
      id="about"
      className="relative border-y border-border/30 bg-gradient-to-b from-background via-primary/[0.02] to-background py-20 dark:via-blue-950/20 lg:py-28"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <motion.div
            className="mb-12 text-center lg:mb-16"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.55 }}
          >
            <div className="flex justify-center">
              <LandingSectionBadge className="mb-4">Our story</LandingSectionBadge>
            </div>
            <h2 className="mb-6 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              <span className="bg-gradient-to-r from-primary via-blue-600 to-cyan-600 bg-clip-text text-transparent">
                About GrowFinx
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              We&apos;re on a mission to revolutionize debt recovery with intelligent automation and
              multi-channel communication
            </p>
          </motion.div>

          <motion.div
            className="prose prose-lg max-w-none mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p className="text-muted-foreground leading-relaxed mb-6">
              GrowFinx was built to transform debt recovery operations. We believe
              that every financial institution deserves access to enterprise-grade
              recovery tools that automate workflows, maximize efficiency, and
              improve recovery rates.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our platform combines multi-channel campaign management, AI-powered
              automation, real-time analytics, and intelligent workflow design to
              help you recover more, faster, and with less manual effort.
            </p>
          </motion.div>

          <motion.div
            ref={ref}
            className="grid md:grid-cols-3 gap-6 lg:gap-8"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {values.map((value, index) => {
              const Icon = value.icon;
              const gradientClass = valueColors[index % valueColors.length];
              return (
                <motion.div key={index} variants={cardVariants}>
                  <Card className="group h-full rounded-2xl border border-border/70 bg-card/95 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-lg">
                    <CardContent className="p-6 text-center">
                      <div
                        className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br ${gradientClass} shadow-lg transition-transform duration-300 group-hover:scale-105`}
                      >
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{value.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

